/* ==========================================================================
   PanelAccommodation > Client > Prototype
   ========================================================================== */

/* Import modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { initRooms, addRoom, destroyRoom } = require('./rooms');
const { initAddRoom } = require('./addRoom');
const { addMessage, removeMessage } = require('./message');
const { validate } = require('./validations');

/* Prototype */
const prototype = {
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            data: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            setData: data => this.store.dispatch(actions.setData(data)),
            addRoom: room => {
                const { maxRooms, maxPeople } = this.config || {};
                const { totalRooms, totalPeople } = this.store.select('data');

                if (totalRooms >= maxRooms || totalPeople >= maxPeople) return;

                this.store.dispatch(actions.addRoom(room));
            },

            removeRoom: room => this.store.dispatch(actions.removeRoom(room)),
            updateRooms: () => {
                const payload = this.rooms.map(room => ({
                    id: room.element.dataset.type,
                    ...room.store.select('data')
                }));

                this.store.dispatch(actions.updateRooms(payload));
            },
            setError: error => this.store.dispatch(actions.setError(error)),
            setInfo: msg => this.store.dispatch(actions.setInfo(msg)),
            removeInfo: () => this.store.dispatch(actions.removeInfo()),
            setValid: () => this.store.dispatch(actions.setValid()),
            enableIncrement: () => this.store.dispatch(actions.enableIncrement()),
            disableIncrement: () => this.store.dispatch(actions.disableIncrement())
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Unsubscribe */
                this.unsubscribe();
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());

                /* Clean DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean reference */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            addRoom: this.store.subscribe((state, action) => {
                if (action.type !== types.ADD_ROOM) return;

                /* Add new room */
                addRoom(this);

                /* Dispatch action, update rooms */
                this.actions.updateRooms();
            }),
            removeRoom: this.store.subscribe((state, action) => {
                if (action.type !== types.REMOVE_ROOM) return;

                /* Destroy room */
                destroyRoom(this, action.payload);

                this.actions.updateRooms();
            }),
            updateRooms: this.store.subscribe((state, action) => {
                if (action.type !== types.UPDATE_ROOMS) return;

                validate(this);
            }),
            setError: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_ERROR) return;

                /* Create error */
                addMessage(this, 'ERROR');
            }),
            setInfo: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_INFO) return;

                addMessage(this, 'INFO');
            }),
            removeInfo: this.store.subscribe((state, action) => {
                if (action.type !== types.REMOVE_INFO) return;

                removeMessage(this);
            }),
            enableIncrement: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE_INCREMENT) return;
                if (!this.rooms || !this.rooms.length) return;

                this.rooms.forEach(room => {
                    room.actions.enableIncrement();
                });
            }),
            disableIncrement: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE_INCREMENT) return;
                if (!this.rooms || !this.rooms.length) return;
                this.rooms.forEach(room => {
                    room.actions.disableIncrement();
                });
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {};

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        /* Start logic */
        if (this.rooms) {
            const payload = this.rooms.map(room => ({ id: room.element.dataset.type, ...room.store.select('data') }));
            this.actions.setData(payload);

            validate(this);
        }

        return this;
    },
    /* Init */
    init() {
        if (!this.config.init) return this;

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* init */
        initRooms(this);

        initAddRoom(this);

        /* Start */
        this.start();

        return this;
    }
};

/* Exports */
module.exports = prototype;
